/**
 * WASM Loader Utility for React Applications
 * Handles loading and initializing WASM modules in the browser
 */

// Import WASM files (you'll need to configure webpack for this)
// import wasmUrl from '../libs/wasm_crypt_bg.wasm?url';

export interface WasmCryptInstance {
  encrypt(data: string): string;
  decrypt(data: string): string;
  free(): void;
}

export interface WasmCryptModule {
  new(key: string): WasmCryptInstance;
}

export interface WasmCryptStatic {
  new(key: string): WasmCryptInstance;
}

let wasmModule: any = null;
let ClientCrypt: WasmCryptStatic | null = null;

/**
 * Initialize WASM module
 * @param wasmPath - Path to the WASM file (optional, uses default if not provided)
 */
export async function initWasm(wasmPath?: string): Promise<void> {
  if (wasmModule && ClientCrypt) {
    console.log('🔧 WASM already initialized');
    return;
  }

  try {
    console.log('🚀 INITIALIZING WASM MODULE...');

    // Method 1: Load from public directory (recommended for React)
    const wasmUrl = wasmPath || '/wasm/wasm_crypt_bg.wasm';
    
    // Fetch the WASM file
    const wasmResponse = await fetch(wasmUrl);
    if (!wasmResponse.ok) {
      throw new Error(`Failed to fetch WASM file: ${wasmResponse.status}`);
    }
    
    const wasmBytes = await wasmResponse.arrayBuffer();
    
    // Method 2: Dynamic import (if you have JS bindings)
    try {
      // Try to import the JS bindings
      const wasmModule = await import('../libs/wasm_crypt.js') as any;

      // Check if the module has the expected functions
      if (wasmModule.__wbg_init && wasmModule.ClientCrypt) {
        // Initialize with the WASM bytes
        await wasmModule.__wbg_init(wasmBytes);
        ClientCrypt = wasmModule.ClientCrypt;
        console.log('✅ WASM MODULE INITIALIZED WITH JS BINDINGS');
      } else {
        throw new Error('JS bindings missing required exports');
      }
    } catch (importError) {
      console.warn('⚠️ JS bindings not found or incomplete, using fallback implementation:', importError);

      // Method 3: Use fallback mock implementation
      ClientCrypt = createClientCryptWrapper(null);
      console.log('✅ WASM MODULE INITIALIZED WITH FALLBACK');
    }

  } catch (error) {
    console.error('❌ FAILED TO INITIALIZE WASM:', error);
    throw new Error(`WASM initialization failed: ${error}`);
  }
}

/**
 * Create a wrapper for WASM functions when JS bindings are not available
 */
function createClientCryptWrapper(_wasmInstance: any): WasmCryptStatic {
  return class ClientCryptWrapper {
    constructor(_key: string) {
      // This is a placeholder - you'll need to implement based on your WASM exports
      console.log('🔑 Creating ClientCrypt instance with key (fallback mode)');
      return this as any;
    }

    static new(key: string): WasmCryptInstance {
      return new ClientCryptWrapper(key) as any;
    }

    encrypt(data: string): string {
      // Call the actual WASM function
      // Example: return wasmInstance.exports.encrypt(data);
      console.log('🔐 FALLBACK Encrypt:', data);
      return btoa(data); // Placeholder
    }

    decrypt(data: string): string {
      // Call the actual WASM function
      // Example: return wasmInstance.exports.decrypt(data);
      console.log('🔓 FALLBACK Decrypt:', data);
      return atob(data); // Placeholder
    }

    free(): void {
      // Clean up WASM memory if needed
      console.log('🧹 Freeing WASM memory (fallback mode)');
    }
  };
}

/**
 * Get the initialized ClientCrypt class
 */
export function getClientCrypt(): WasmCryptStatic {
  if (!ClientCrypt) {
    throw new Error('WASM not initialized. Call initWasm() first.');
  }
  return ClientCrypt;
}

/**
 * Check if WASM is initialized
 */
export function isWasmInitialized(): boolean {
  return ClientCrypt !== null;
}

/**
 * Load WASM from different sources based on environment
 */
export async function loadWasmFromEnvironment(): Promise<void> {
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  if (isDevelopment) {
    // In development, load from public directory
    await initWasm('/wasm/wasm_crypt_bg.wasm');
  } else {
    // In production, you might want to load from CDN or bundled assets
    await initWasm('/static/wasm/wasm_crypt_bg.wasm');
  }
}
